import { getApp } from "firebase/app";
import {
  collection,
  doc,
  documentId,
  getDoc,
  getCountFromServer,
  getDocs,
  limit,
  orderBy,
  query,
  updateDoc,
  arrayUnion,
  where,
  type Timestamp,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { auth, db } from "../../lib/firebase/config";
import { getISOWeekKey, previousISOWeekKey } from "../../utils/isoWeek";
import { withReadTimeout } from "../../lib/firebase/readTimeout";
import type {
  SeasonLadderEntry,
  SeasonStarId,
  SeasonStars,
} from "../../features/leagues/utils/leagueSeasons";

// ── Tipos exportados ───────────────────────────────────────────────────────────

export type LeagueTier = "bronze" | "silver" | "gold" | "diamond";

export type RankingMetric = "xp" | "pages" | "focusMin";

export interface LeagueRoomEntryStats {
  xp: number;
  focusMin: number;
  pages: number;
}

export interface LeagueRoomEntry {
  displayName: string;
  photoURL: string;
  /** Placar da SEMANA (zera na virada). Espelho de `functions/src/shared/types.ts`. */
  stats: LeagueRoomEntryStats;
  /**
   * Placar de SEMPRE — só em sala privada, nunca zerado (10/08).
   * ⚠️ Ausente em entry antiga e em toda sala pública. Leia por
   * `resolveLifetimeStats`, nunca cru: em sala privada anterior a 10/08 o
   * `stats` legado JÁ ERA o acumulado de vida, e ler `lifetime` direto mostraria
   * zero para quem tem quatro meses de placar.
   */
  lifetime?: LeagueRoomEntryStats;
  // Identidade visual PRO do membro no momento da entrada na sala (snapshot).
  // Opcionais para retro-compat com entries pré-08/05/2026.
  isPro?: boolean;
  legacyTags?: string[];
  usernameColor?: string;
  flairs?: string[];
  /**
   * Moldura equipada no momento do crédito. A entry é reescrita a cada
   * atualização de XP, então converge em horas — entry parada de semana antiga
   * simplesmente não traz o campo e cai no anel de tier.
   */
  avatarBorder?: string;
}

export interface LeagueRoomDoc {
  roomId: string;
  league: LeagueTier;
  week: string;
  type?: "public" | "private";
  memberCount: number;
  isOpen: boolean;
  name?: string;
  /** Capa da liga privada (Cloudinary). Ausente/`""` = sem foto, cai nas iniciais. */
  photoUrl?: string;
  inviteCode?: string;
  ownerId?: string;
  rankingMetric?: RankingMetric;
  entries: Record<string, LeagueRoomEntry>;
  /**
   * Clube do livro (`docs/systems/league-club-book.md` §2). AUSENTE = liga
   * livre: leia sempre `room.club?.enabled === true`, nunca a presença.
   */
  club?: LeagueRoomClub;
}

/**
 * Placar "de sempre" de uma entry de liga privada.
 *
 * O fallback para `stats` não é defensividade genérica: antes de 10/08 a sala
 * privada nunca zerava, então o `stats` daquelas salas É o acumulado histórico.
 * Sem o fallback, o dono da liga de abril veria 0 no dia do deploy.
 */
export function resolveLifetimeStats(entry: LeagueRoomEntry): LeagueRoomEntryStats {
  return entry.lifetime ?? entry.stats;
}

/** Entrada já enriquecida com rank e uid para renderização. */
export interface RankedEntry extends LeagueRoomEntry {
  uid: string;
  rank: number;
}

export interface UpdateBookProgressResult {
  pagesLogged: number;
  /** XP creditado nesta chamada — inclui o de uma sessão de Foco liberada por ela. */
  xpAwarded: number;
  /**
   * Esta marcação foi a declaração atrasada de uma sessão de Foco sem páginas
   * (BUG-310): a sessão recebeu `xp` — o maior entre o tempo e estas páginas —
   * e a marcação não pagou XP de página própria. Ausente = nada foi liberado.
   */
  focusXpReleased?: { sessionId: string; xp: number; via: "minutes" | "pages" };
  limitReached: boolean;
  dailyPageXp: number;
  /** Streak após esta atualização. Server-side a partir da Sprint E. */
  currentStreak: number;
  /** True quando esta chamada incrementou a streak hoje. */
  streakIncremented: boolean;
  /**
   * Este registro foi o 1º dia lido da vida da conta (primeira semana, 07/10).
   * Ausente em functions anteriores — trate como `false`.
   */
  firstRead?: boolean;
  /**
   * A chamada só reparou um progresso Kindle corrompido (`progress > 100`) e
   * saiu sem creditar. A UI trata como sucesso, nunca como erro.
   */
  repaired?: boolean;
  /** O alvo era MENOR que o progresso atual — correção pra baixo, nada é debitado. */
  progressLowered?: boolean;
  /**
   * O alvo está dentro de território já creditado (≤ marca d'água do servidor):
   * o progresso foi gravado, mas a economia não pagou. Desfecho normal de
   * re-subir um trecho já pago — sucesso silencioso, nunca erro.
   */
  belowHighwater?: boolean;
  /**
   * O livro foi fechado com data que NÃO é hoje (ou sem data): progresso
   * gravado, nada creditado no dia. A UI precisa dizer isso em voz alta —
   * marcar um livro como lido e não ver ofensiva nenhuma, em silêncio, lê
   * como bug.
   */
  retroactiveCompletion?: boolean;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const functionsRegion = import.meta.env.VITE_FIREBASE_FUNCTIONS_REGION as
  | string
  | undefined;

function getFunctionsInstance() {
  const app = getApp();
  return functionsRegion?.trim()
    ? getFunctions(app, functionsRegion.trim())
    : getFunctions(app);
}

// ── Callable ──────────────────────────────────────────────────────────────────

export interface CreatePrivateLeagueResult {
  roomId: string;
  inviteCode: string;
}

export interface JoinPrivateLeagueResult {
  roomId: string;
  name: string;
}

export interface RemovePrivateLeagueMemberResult {
  roomId: string;
  targetUid: string;
  removed: true;
}

export async function createPrivateLeagueCallable(
  name: string,
  rankingMetric: RankingMetric,
  photoUrl?: string,
): Promise<CreatePrivateLeagueResult> {
  if (!auth.currentUser) throw new Error("Utilizador não autenticado.");
  const fn = httpsCallable<
    {name: string; rankingMetric: RankingMetric; photoUrl?: string},
    CreatePrivateLeagueResult
  >(getFunctionsInstance(), "createPrivateLeague");
  const res = await fn({name, rankingMetric, ...(photoUrl ? {photoUrl} : {})});
  return res.data;
}

export async function joinPrivateLeagueCallable(
  inviteCode: string,
): Promise<JoinPrivateLeagueResult> {
  if (!auth.currentUser) throw new Error("Utilizador não autenticado.");
  const fn = httpsCallable<{inviteCode: string}, JoinPrivateLeagueResult>(
    getFunctionsInstance(),
    "joinPrivateLeague",
  );
  const res = await fn({inviteCode});
  return res.data;
}

/**
 * Prévia do convite (C0 — `docs/systems/private-league-invite-preview.md`).
 * Só com roomId + código certos; a sala privada não é legível por não-membra
 * desde o BUG-307, então este é o único caminho até o nome/fotos da liga.
 */
export type PrivateLeagueInvitePreview =
  | { isMember: true; roomId: string }
  | {
      isMember: false;
      roomId: string;
      name: string;
      photoUrl?: string;
      initials: string;
      memberCount: number;
      ownerFirstName?: string;
      avatars: { photoURL?: string; initial: string }[];
      isFull: boolean;
      userAtLimit: boolean;
    };

export async function previewPrivateLeagueInviteCallable(
  roomId: string,
  code: string,
): Promise<PrivateLeagueInvitePreview> {
  if (!auth.currentUser) throw new Error("Utilizador não autenticado.");
  const fn = httpsCallable<{roomId: string; code: string}, PrivateLeagueInvitePreview>(
    getFunctionsInstance(),
    "previewPrivateLeagueInvite",
  );
  const res = await fn({roomId, code});
  return res.data;
}

export async function removePrivateLeagueMemberCallable(
  roomId: string,
  targetUid: string,
): Promise<RemovePrivateLeagueMemberResult> {
  if (!auth.currentUser) throw new Error("Utilizador não autenticado.");
  const fn = httpsCallable<
    {roomId: string; targetUid: string},
    RemovePrivateLeagueMemberResult
  >(getFunctionsInstance(), "removePrivateLeagueMember");
  const res = await fn({roomId, targetUid});
  return res.data;
}

// ── Ciclo de vida da liga privada (pendência 20 — 10/08) ─────────────────────

export interface PrivateLeagueMutationResult { roomId: string }

async function callLeague<TIn extends object, TOut>(
  name: string,
  payload: TIn,
): Promise<TOut> {
  if (!auth.currentUser) throw new Error("Utilizador não autenticado.");
  const fn = httpsCallable<TIn, TOut>(getFunctionsInstance(), name);
  const res = await fn(payload);
  return res.data;
}

export function updatePrivateLeagueCallable(
  roomId: string,
  patch: {name?: string; rankingMetric?: RankingMetric; photoUrl?: string},
): Promise<PrivateLeagueMutationResult> {
  return callLeague("updatePrivateLeague", {roomId, ...patch});
}

export function transferPrivateLeagueOwnershipCallable(
  roomId: string,
  targetUid: string,
): Promise<{roomId: string; ownerId: string}> {
  return callLeague("transferPrivateLeagueOwnership", {roomId, targetUid});
}

export function leavePrivateLeagueCallable(
  roomId: string,
): Promise<{roomId: string; left: true}> {
  return callLeague("leavePrivateLeague", {roomId});
}

export function deletePrivateLeagueCallable(
  roomId: string,
): Promise<{roomId: string; deleted: true; membersCleared: number}> {
  return callLeague("deletePrivateLeague", {roomId});
}

export function regeneratePrivateLeagueInviteCodeCallable(
  roomId: string,
): Promise<{roomId: string; inviteCode: string}> {
  return callLeague("regeneratePrivateLeagueInviteCode", {roomId});
}

// ── Clube do livro nas ligas privadas (F2 · 05/10) ───────────────────────────
//
// Blueprint: `docs/systems/league-club-book.md`. Sem economia (§0.6): nada
// aqui paga ou cobra moeda/XP. Gate de PRO é da DONA, no momento de ABRIR um
// livro (`details.reason === "pro_required"`); encerrar é grátis.
// ⚠️ Pote, estante e histórico se leem com getDoc/getDocs — NUNCA onSnapshot
// (cada avaliação das rules custa 1 read extra no `isLeagueMember`).

export type ClubOutcome = "finished" | "abandoned";
export type ClubOpenedVia = "spin" | "pick" | "lura" | "vote";
/** C6 (D6) — o combinado de como sai o próximo livro (só exibido; ausente = "spin"). */
export type ClubNextVia = "spin" | "vote" | "pick";
export type ClubPotOrigin = "member" | "lura";

/** Snapshot do livro aberto (catálogo no momento da abertura). */
export interface ClubBookSnapshot {
  workId: string;
  canonicalBookId: string;
  title: string;
  author: string;
  coverUrl: string;
  totalPages: number | null;
  /** Uids das padrinhas que eram membras na abertura (sem "lura"). Nome/foto: `room.entries`. */
  sponsors: string[];
}

export interface ClubCurrent {
  cycleKey: string;
  cycleIndex: number;
  /** Dia BR "YYYY-MM-DD". O prazo é META exibida, nada trava por data. */
  startDayKey: string;
  endDayKey: string;
  openedBy: string;
  openedAtMs: number;
  openedVia: ClubOpenedVia;
  book: ClubBookSnapshot;
  /** C6 — só aberturas pelo voto: "7 de 12 deram ♥"; `tiedWith` = desempate no pote. */
  vote?: {winnerVotes: number; electorate: number; tiedWith?: string[]};
}

/** C6 — "Este é o próximo livro" (vale só se `isNextPickValid`). */
export interface ClubNextPick {
  potKey: string;
  addedAtMs: number;
  markedBy: string;
  markedAtMs: number;
}

/** O `club` do doc da sala. */
export interface LeagueRoomClub {
  enabled: boolean;
  cycleDays: 15 | 30;
  /** Nº de livros já abertos — é o `expectedCycleCount` que a abertura manda. */
  cycleCount: number;
  dayOpens: {dayKey: string; n: number};
  current: ClubCurrent | null;
  /** C6 — ausente = "spin". */
  nextVia?: ClubNextVia;
  nextPick?: ClubNextPick;
  /** C6 — a chamada para votar já saiu para esta abertura (`forCycleIndex === cycleCount + 1`). */
  voteCall?: {forCycleIndex: number; atMs: number; by: string};
}

export interface ClubPotItem {
  potKey: string;
  workId: string;
  canonicalBookId: string;
  title: string;
  author: string;
  coverUrl: string;
  totalPages: number | null;
  /** "lura" = selo "Sugestão da Lura". */
  origin: ClubPotOrigin;
  /** Uids; a sentinela "lura" aparece no item da Lura e não é pessoa. */
  sponsors: string[];
  luraReason?: string;
  /** Geração do item (o ♥ vale só para ela). */
  addedAtMs: number;
  /** C6 — ♥ explícitos (de quem não colocou); display. Ausente = 0. */
  votes?: number;
  /** C6 — "fora da roleta": continua no pote e no voto. */
  outOfSpin?: true;
}

export interface ClubPotDoc {
  items: Record<string, ClubPotItem>;
  /** Obras que não voltam ao pote (terminadas + a aberta agora). */
  drawnWorkIds: string[];
}

export interface ClubShelfEntry {
  cycleKey: string;
  workId: string;
  title: string;
  author: string;
  coverUrl: string;
  outcome: ClubOutcome;
  openedDayKey: string;
  closedDayKey: string;
  /** "9 de 12 terminaram" — só contagem, do último cache de progresso (F4). */
  tally?: {finished: number; total: number};
}

export interface ClubShelfDoc {
  entries: ClubShelfEntry[];
}

export interface ClubFinalTally {
  finished: number;
  reading: number;
  queued: number;
  none: number;
  total: number;
  fromCacheAtMs: number;
}

export interface ClubCycleBook extends ClubBookSnapshot {
  potKey: string;
  origin: ClubPotOrigin;
}

/** `league_rooms/{roomId}/club_cycles/{cycleKey}` — histórico. */
export interface ClubCycleDoc {
  spinId: string;
  cycleKey: string;
  cycleIndex: number;
  startDayKey: string;
  endDayKey: string;
  cycleDays: number;
  openedBy: string;
  openedAtMs: number;
  openedVia: ClubOpenedVia;
  ownerWasPro: true;
  book: ClubCycleBook;
  editionIds: string[];
  /** C6 — só `openedVia: "vote"`. Contagens, nunca quem votou. */
  voteTally?: ClubVoteTally;
  status: "open" | "closed";
  outcome?: ClubOutcome;
  closedBy?: string;
  closedAtMs?: number;
  closedDayKey?: string;
  finalTally?: ClubFinalTally;
  /** F6 — recado da dona ao encerrar (gravado uma vez, junto do desfecho). */
  closedNote?: string;
  /** F6 — servidor: o aviso "livro da vez" deste ciclo já saiu (idempotência). */
  announcedAtMs?: number;
  /** F6 — servidor: o recado deste ciclo já viajou no aviso do seguinte. */
  closedNoteAnnouncedAtMs?: number;
  winnerIndex?: number;
  candidates?: {potKey: string; workId: string; title: string; origin: ClubPotOrigin; sponsors: string[]}[];
}

/** C6 — placar de uma abertura pelo voto. */
export interface ClubVoteTally {
  winnerVotes: number;
  electorate: number;
  top: {potKey: string; workId: string; title: string; origin: ClubPotOrigin; votes: number}[];
  tieBreak?: "draw";
}

export interface SetPrivateLeagueModeResult {
  roomId: string;
  mode: "free" | "club";
  enabled: boolean;
  cycleDays: 15 | 30;
  nextVia: ClubNextVia;
  /** `false` = nada mudou (idempotente). */
  changed: boolean;
}

export interface AddClubPotBookResult {
  roomId: string;
  potKey: string;
  /** `true` = já era padrinha (nada gravado). */
  alreadySponsor: boolean;
  pot: ClubPotDoc;
}

export interface RemoveClubPotBookResult {
  roomId: string;
  potKey: string;
  removed: "item" | "sponsorship" | "none";
  pot: ClubPotDoc;
}

export interface ClubClosedSummary {
  cycleKey: string;
  outcome: ClubOutcome;
  closedDayKey: string;
  finalTally: ClubFinalTally | null;
}

export interface OpenClubBookResult {
  roomId: string;
  cycleKey: string;
  spinId: string;
  /** `true` = replay (já tinha sido aberto; nada gravado). */
  alreadyOpened: boolean;
  openedVia: ClubOpenedVia;
  cycleCount: number;
  startDayKey: string;
  endDayKey: string;
  book: ClubCycleBook;
  /** O livro encerrado junto (`closeCurrent`); `null` sem ele e no replay. */
  closed: ClubClosedSummary | null;
}

export interface CloseClubCycleResult {
  roomId: string;
  cycleKey: string;
  outcome: ClubOutcome;
  /** `true` = já estava encerrado com este desfecho (nada gravado). */
  alreadyClosed: boolean;
  closedDayKey: string;
  finalTally: ClubFinalTally | null;
}

export function setPrivateLeagueModeCallable(
  input: {roomId: string; mode: "free" | "club"; cycleDays?: 15 | 30; nextVia?: ClubNextVia},
): Promise<SetPrivateLeagueModeResult> {
  return callLeague("setPrivateLeagueMode", input);
}

export function addClubPotBookCallable(
  input: {roomId: string; canonicalBookId: string},
): Promise<AddClubPotBookResult> {
  return callLeague("addClubPotBook", input);
}

export function removeClubPotBookCallable(
  input: {roomId: string; potKey: string},
): Promise<RemoveClubPotBookResult> {
  return callLeague("removeClubPotBook", input);
}

/**
 * Desfecho do livro atual ao abrir outro. `note` = recado ao clube (F6): 1–140
 * caracteres, uma linha, sem link (`invalid-argument` com `details.reason`
 * `note_has_link` / `note_has_newline`). Vai junto no aviso "livro da vez".
 */
export interface ClubCloseCurrent {
  outcome: ClubOutcome;
  note?: string;
}

/** Teto do recado (espelho de `CLUB_NOTE_MAX` em `functions/src/leagues/club/schemas.ts`). */
export const CLUB_NOTE_MAX = 140;

export function openClubBookCallable(
  input: {roomId: string; expectedCycleCount: number; potKey: string; closeCurrent?: ClubCloseCurrent},
): Promise<OpenClubBookResult> {
  return callLeague("openClubBook", input);
}

/** Encerrar sozinho NÃO avisa ninguém; o `note` fica no ciclo e viaja no aviso da próxima abertura. */
export function closeClubCycleCallable(
  input: {roomId: string; cycleKey: string; outcome: ClubOutcome; note?: string},
): Promise<CloseClubCycleResult> {
  return callLeague("closeClubCycle", input);
}

// ── F3 · roleta ──────────────────────────────────────────────────────────────
// O sorteio é do SERVIDOR (`crypto.randomInt`) e fica gravado; o client só
// ENCENA: gira sobre `candidates` (já na ordem do sorteio) e para em
// `candidates[winnerIndex]`. Copy: "sorteia", nunca "ganhou"/"prêmio".

/** Um candidato do giro (`club_cycles.candidates`), ordenado por `potKey`. */
export type ClubSpinCandidate = NonNullable<ClubCycleDoc["candidates"]>[number];

export interface SpinClubBookResult {
  roomId: string;
  cycleKey: string;
  spinId: string;
  /** `true` = replay (o giro já tinha acontecido; mesmo resultado, nada gravado). */
  alreadySpun: boolean;
  /** `candidates[winnerIndex]`. */
  winner: ClubSpinCandidate;
  candidates: ClubSpinCandidate[];
  winnerIndex: number;
  startDayKey: string;
  endDayKey: string;
  cycleCount: number;
  book: ClubCycleBook;
  /** O livro encerrado junto (`closeCurrent`); `null` sem ele e no replay. */
  closed: ClubClosedSummary | null;
}

/** Roleta. Mesmo contrato de abertura do `openClubBookCallable`, sem `potKey`. */
export function spinClubBookCallable(
  input: {roomId: string; expectedCycleCount: number; closeCurrent?: ClubCloseCurrent},
): Promise<SpinClubBookResult> {
  return callLeague("spinClubBook", input);
}

// ── F4 · progresso do clube ──────────────────────────────────────────────────
// `club/progress` é ilegível pelas rules: a única porta é esta callable (cache
// de 30 min no servidor). Sem ordem de chegada: por membra só `{state, pct}`,
// `members` é mapa — a UI ordena por nome. A linha "Você" vem da estante local.

export type ClubMemberProgressState = "none" | "queued" | "reading" | "finished" | "readBefore";

export interface ClubProgressResult {
  /** `null` = nenhum livro aberto (e `members` vazio). */
  cycleKey: string | null;
  members: Record<string, {state: ClubMemberProgressState; pct: number | null}>;
  /** Cache anterior: outra chamada está recalculando agora. */
  stale?: boolean;
}

export function refreshClubProgressCallable(input: {roomId: string}): Promise<ClubProgressResult> {
  return callLeague("refreshClubProgress", input);
}

// ── F5 · sugestão da Lura ────────────────────────────────────────────────────
// A Lura gera SOZINHA depois de cada abertura (para o próximo ciclo) e a
// sugestão chega ao client como item do pote (`origin: "lura"`, `luraReason`).
// Esta callable é a "tentar de novo" da dona: devolve a gravada sem chamar a
// IA, e só chama de novo com `retryAi` depois de um fallback (2 por ciclo).
// Erros: `pro_required` (feature `club_lura`), `club_too_small`, `stale_state`,
// `generating`, `lura_attempts`.

export type ClubLuraSuggestionStatus = "generating" | "ready" | "fallback" | "consumed" | "expired";

export interface ClubLuraSuggestionItem {
  potKey: string;
  workId: string;
  canonicalBookId: string;
  title: string;
  author: string;
  coverUrl: string;
  totalPages: number | null;
}

export interface ClubLuraSuggestionResult {
  roomId: string;
  /** A abertura para a qual foi gerada (= `club.cycleCount + 1`). */
  forCycleIndex: number;
  status: ClubLuraSuggestionStatus;
  /** "gemini" = a Lura escolheu · "common_want" = Quero Ler em comum (sem IA) · `null` = nada em comum. */
  source: "gemini" | "common_want" | null;
  item: ClubLuraSuggestionItem | null;
  /** O porquê, sempre de GRUPO (nunca nome, nunca "você"). */
  reason: string;
  reasonKind: "ai" | "template";
  /** Só rótulos e contagens agregados — nada por pessoa. */
  basis: {genres: string[]; memberCountUsed: number; commonWantCount?: number};
  aiAttempts: number;
  /** `true` = já existia (nenhuma chamada à IA). */
  alreadyGenerated: boolean;
  /** Dá para pedir `retryAi` (caiu no fallback e ainda há tentativa no ciclo). */
  canRetryAi: boolean;
}

// ── C6 · ♥ no pote, porta do voto, marcas e chamada (13/10, §15) ─────────────
// O voto não é público: `club_votes/{uid}` só a própria pessoa lê (getDoc,
// nunca onSnapshot). Quem decide o livro é a recontagem no servidor, não o
// número da tela. Erros: `pot_item_missing`, `vote_rate_limit`, `no_votes`,
// `vote_tie` (`details.tied`), `pot_empty`, `pro_required` (feature `club_vote`).

export interface SetClubPotVoteResult {
  roomId: string;
  potKey: string;
  voted: boolean;
  /** A pessoa colocou o livro: o ♥ já conta (travado na tela). */
  implicit: boolean;
  votes: number;
}

export function setClubPotVoteCallable(
  input: {roomId: string; potKey: string; vote: boolean},
): Promise<SetClubPotVoteResult> {
  return callLeague("setClubPotVote", input);
}

export type OpenClubBookByVoteResult = OpenClubBookResult & {
  voteTally: ClubVoteTally;
  winnerIndex?: number;
  candidates?: ClubSpinCandidate[];
};

/** Empate no topo: `onTie` ausente/"ask" → erro `vote_tie` (sem gravar); "draw" → o pote desempata. */
export function openClubBookByVoteCallable(
  input: {roomId: string; expectedCycleCount: number; closeCurrent?: ClubCloseCurrent; onTie?: "ask" | "draw"},
): Promise<OpenClubBookByVoteResult> {
  return callLeague("openClubBookByVote", input);
}

/** Os empatados de um `vote_tie` (`details.tied`). */
export interface ClubVoteTieEntry {
  potKey: string;
  title: string;
  origin: ClubPotOrigin;
  votes: number;
}

export function clubVoteTie(err: unknown): ClubVoteTieEntry[] | null {
  const details = (err as {details?: unknown} | null)?.details as {reason?: unknown; tied?: unknown} | undefined;
  if (details?.reason !== "vote_tie" || !Array.isArray(details.tied)) return null;
  return details.tied as ClubVoteTieEntry[];
}

export function setClubNextPickCallable(
  input: {roomId: string; potKey: string | null},
): Promise<{roomId: string; nextPick: ClubNextPick | null; changed: boolean}> {
  return callLeague("setClubNextPick", input);
}

export function setClubPotOutOfSpinCallable(
  input: {roomId: string; potKey: string; excluded: boolean},
): Promise<{roomId: string; potKey: string; excluded: boolean; changed: boolean}> {
  return callLeague("setClubPotOutOfSpin", input);
}

export interface CallClubVoteResult {
  roomId: string;
  forCycleIndex: number;
  alreadyCalled: boolean;
  recipients: number;
  sent: number;
  skipped: number;
  failed: number;
}

export function callClubVoteCallable(input: {roomId: string}): Promise<CallClubVoteResult> {
  return callLeague("callClubVote", input);
}

/** Os ♥ da própria pessoa nesta sala (1 read; só ela lê). `{}` = nenhum. Nunca onSnapshot. */
export async function fetchMyClubVotes(roomId: string, uid: string): Promise<Record<string, number>> {
  const snap = await withReadTimeout(getDoc(doc(db, "league_rooms", roomId, "club_votes", uid)));
  if (!snap.exists()) return {};
  const hearts = (snap.data() as {hearts?: Record<string, number>}).hearts;
  return hearts && typeof hearts === "object" ? hearts : {};
}

export function requestClubLuraSuggestionCallable(
  input: {roomId: string; expectedCycleCount: number; retryAi?: boolean},
): Promise<ClubLuraSuggestionResult> {
  return callLeague("requestClubLuraSuggestion", input);
}

/**
 * `details.reason` de um erro das callables do clube (`pro_required`,
 * `cycle_open`, `stale_state`, `opens_per_day`, `pot_member_cap`, `pot_full`,
 * `pot_rate_limit`, `already_drawn`, `book_not_cataloged`, `club_disabled`,
 * `not_member`, `not_sponsor`, `pot_item_missing`, `already_closed`,
 * `cycle_not_found`, `pot_too_small`, `spin_failed`, `note_has_link`,
 * `note_has_newline`, `club_too_small`, `generating`, `lura_attempts`). `null` quando o erro
 * não traz motivo.
 */
export function clubErrorReason(err: unknown): string | null {
  const details = (err as {details?: unknown} | null)?.details;
  if (details && typeof details === "object") {
    const reason = (details as {reason?: unknown}).reason;
    if (typeof reason === "string") return reason;
  }
  return null;
}

/** Pote do clube (1 read). `null` = ainda inexistente. Nunca onSnapshot. */
export async function fetchClubPot(roomId: string): Promise<ClubPotDoc | null> {
  const snap = await withReadTimeout(getDoc(doc(db, "league_rooms", roomId, "club", "pot")));
  if (!snap.exists()) return null;
  const data = snap.data() as Partial<ClubPotDoc>;
  const items: Record<string, ClubPotItem> = {};
  for (const [k, item] of Object.entries(data.items ?? {})) items[k] = {...item, potKey: item.potKey ?? k};
  return {items, drawnWorkIds: Array.isArray(data.drawnWorkIds) ? data.drawnWorkIds : []};
}

/** Estante do clube (1 read). `null` = nenhum livro encerrado ainda. Nunca onSnapshot. */
export async function fetchClubShelf(roomId: string): Promise<ClubShelfDoc | null> {
  const snap = await withReadTimeout(getDoc(doc(db, "league_rooms", roomId, "club", "shelf")));
  if (!snap.exists()) return null;
  const data = snap.data() as Partial<ClubShelfDoc>;
  return {entries: Array.isArray(data.entries) ? data.entries : []};
}

/** Histórico do clube, do mais recente ao mais antigo (1 read por ciclo). Nunca onSnapshot. */
export async function fetchClubCycles(roomId: string, max = 24): Promise<ClubCycleDoc[]> {
  const snap = await withReadTimeout(getDocs(query(
    collection(db, "league_rooms", roomId, "club_cycles"),
    orderBy("cycleIndex", "desc"),
    limit(max),
  )));
  return snap.docs.map((d) => d.data() as ClubCycleDoc);
}

// ── Navegação entre ligas (pendência 21 — 11/08) ─────────────────────────────

/**
 * Quantas salas de uma divisão são lidas para montar o retrato dela.
 *
 * ⚠️ **É um teto declarado, não "todas".** Uma divisão pode ter dezenas de
 * salas numa base grande, e cada doc de sala carrega até 30 entries com
 * snapshot PRO (a maior da base hoje tem 7,7 KB). Ler a divisão inteira faria
 * o custo crescer com o tamanho do beta, num ecrã que é consulta e não ação.
 * Com o teto, o número de leitores vira "pelo menos N" — e a UI diz isso, em
 * vez de mentir um total exato.
 */
export const DIVISION_ROOM_SAMPLE = 10;

/** Amigos lidos de uma vez na busca de "onde eles estão". */
export const FRIENDS_LOOKUP_CAP = 30;

/**
 * Os campos de identidade visual que viajam no snapshot da entry.
 *
 * Existe como TIPO, e não como lista à mão em cada superfície, porque é assim
 * que uma delas esquece um campo: o pódio da divisão copiava `uid`,
 * `displayName`, `photoURL` e `xp` e descartava os cinco abaixo em silêncio —
 * nada quebra, o avatar só cai no anel de tier. Campo novo aqui é campo novo
 * em toda superfície que usa `pickLeagueIdentity`.
 */
export type LeagueIdentityFields = Pick<
  LeagueRoomEntry,
  "isPro" | "legacyTags" | "usernameColor" | "flairs" | "avatarBorder"
>;

/**
 * Extrai a identidade de uma entry. Espelho client do `extractEntryProSnapshot`
 * do servidor — lá para GRAVAR, aqui para REPASSAR ao `UserAvatar`/`UserName`.
 */
export function pickLeagueIdentity(entry: LeagueRoomEntry): LeagueIdentityFields {
  return {
    isPro: entry.isPro,
    legacyTags: entry.legacyTags,
    usernameColor: entry.usernameColor,
    flairs: entry.flairs,
    avatarBorder: entry.avatarBorder,
  };
}

export interface DivisionPodiumEntry extends LeagueIdentityFields {
  uid: string;
  displayName: string;
  photoURL: string;
  xp: number;
}

export interface DivisionSnapshot {
  tier: LeagueTier;
  /** Salas públicas desta divisão lidas nesta consulta (≤ DIVISION_ROOM_SAMPLE). */
  roomsSampled: number;
  /** Havia mais salas do que o teto — o total exibido é um piso, não o total. */
  truncated: boolean;
  /** Leitores encontrados nas salas lidas. */
  readers: number;
  /** Os 3 maiores XP entre as salas lidas. */
  podium: DivisionPodiumEntry[];
}

/**
 * Retrato de uma divisão nesta semana.
 *
 * ⚠️ Isto NÃO é "espiar uma sala de Ouro" — a decisão de 02/08 que recusou
 * aquilo continua de pé, e pelo mesmo motivo: uma sala qualquer de Ouro é uma
 * lista de 30 estranhos que não é a disputa de ninguém. O que se mostra aqui é
 * a **divisão**: quanta gente está nela e quem está na frente. Regras e prêmios
 * (a parte que nunca fica vazia) saem do espelho local, sem leitura nenhuma.
 *
 * `league_rooms` é `allow read: if isAuthed()`, então não há callable nem
 * mudança de rules. A query é 3 igualdades — medido contra produção, não exige
 * índice composto novo.
 */
export async function fetchDivisionSnapshot(
  tier: LeagueTier,
): Promise<DivisionSnapshot> {
  const week = getISOWeekKey();
  // Teto obrigatório: `getDocs` espera o SERVIDOR e, com o transporte do
  // Firestore morto (BUG-179), não assenta nunca — a folha da divisão fica em
  // "Carregando…" para sempre, que é indistinguível de modal travado. Este
  // arquivo ficou de fora da varredura de 29/08 que pôs o teto nos outros
  // one-shots; o `catch` do `DivisionSheet` já existe e degrada certo.
  const snap = await withReadTimeout(getDocs(
    query(
      collection(db, "league_rooms"),
      where("league", "==", tier),
      where("week", "==", week),
      where("type", "==", "public"),
      limit(DIVISION_ROOM_SAMPLE + 1),
    ),
  ));

  const docs = snap.docs.slice(0, DIVISION_ROOM_SAMPLE);
  const all: DivisionPodiumEntry[] = [];
  for (const d of docs) {
    const room = d.data() as LeagueRoomDoc;
    for (const [uid, entry] of Object.entries(room.entries ?? {})) {
      all.push({
        uid,
        displayName: entry.displayName ?? "Leitor",
        photoURL: entry.photoURL ?? "",
        xp: entry.stats?.xp ?? 0,
        ...pickLeagueIdentity(entry),
      });
    }
  }
  all.sort((a, b) => b.xp - a.xp);

  return {
    tier,
    roomsSampled: docs.length,
    truncated: snap.docs.length > DIVISION_ROOM_SAMPLE,
    readers: all.length,
    podium: all.slice(0, 3),
  };
}

export interface FriendLeaguePlacement {
  uid: string;
  displayName: string;
  username: string | null;
  photoURL: string;
  /** `null` = a pessoa ainda não entrou em liga nenhuma. */
  tier: LeagueTier | null;
  /** Está na MESMA sala que quem perguntou, nesta semana. */
  sameRoom: boolean;
}

/**
 * Onde estão as pessoas que você segue.
 *
 * Lê `users/{uid}` em lotes de 10 por `documentId() in` — o mesmo padrão do
 * `friendBooksService`. Teto de `FRIENDS_LOOKUP_CAP`: quem segue muita gente vê
 * os primeiros, o que é degradação aceitável para um atalho social (e é a mesma
 * escolha que o `useFollowingSet` já faz com o `limit(100)` dele).
 *
 * ⚠️ A liga vem de `users.currentLeague`, não de varredura de salas: é 1 leitura
 * por pessoa em vez de N salas, e responde exatamente a pergunta ("em que
 * divisão fulano está"). `currentRoomId` só é comparado quando é da semana
 * corrente — ponteiro de semana passada apontaria "mesma sala" para uma sala
 * que já fechou.
 */
export async function fetchFriendsLeaguePlacement(
  followingIds: readonly string[],
  myRoomId: string | null,
): Promise<FriendLeaguePlacement[]> {
  const ids = followingIds.slice(0, FRIENDS_LOOKUP_CAP);
  if (ids.length === 0) return [];

  const week = getISOWeekKey();
  const out: FriendLeaguePlacement[] = [];

  for (let i = 0; i < ids.length; i += 10) {
    const chunk = ids.slice(i, i + 10);
    const snap = await withReadTimeout(getDocs(
      query(collection(db, "users"), where(documentId(), "in", chunk)),
    ));
    for (const d of snap.docs) {
      const u = d.data() as {
        displayName?: string; username?: string; photoURL?: string;
        currentLeague?: LeagueTier; currentRoomId?: string | null;
        currentRoomWeek?: string | null;
      };
      out.push({
        uid: d.id,
        displayName: u.displayName || u.username || "Leitor",
        username: u.username ?? null,
        photoURL: u.photoURL ?? "",
        tier: u.currentLeague ?? null,
        sameRoom:
          myRoomId != null &&
          u.currentRoomWeek === week &&
          u.currentRoomId === myRoomId,
      });
    }
  }

  // Quem está em liga primeiro, depois alfabético — a lista responde "quem eu
  // alcanço" antes de "quem ainda não começou".
  const order: Record<string, number> = {diamond: 0, gold: 1, silver: 2, bronze: 3};
  out.sort((a, b) => {
    const ta = a.tier ? order[a.tier] ?? 9 : 9;
    const tb = b.tier ? order[b.tier] ?? 9 : 9;
    return ta !== tb ? ta - tb : a.displayName.localeCompare(b.displayName, "pt-BR");
  });
  return out;
}

/**
 * Chama a Cloud Function updateBookProgress.
 * Valida progresso server-side, aplica XP com cap diário de 200 e atribui sala.
 * Retorna o resultado de XP para feedback visual.
 */
export async function updateBookProgressXP(
  userBookId: string,
  newProgress: number,
  opts?: {
    format?: "physical" | "kindle";
    totalPages?: number;
    /**
     * Dia declarado de conclusão (`YYYY-MM-DD`) quando ESTA chamada fecha o
     * livro. A CF usa para decidir se a leitura alimenta o dia — conclusão
     * retroativa (ou sem data) grava progresso e não credita nada.
     *
     * Omitir aqui é o mesmo que declarar "não terminei hoje": o servidor
     * trata ausência como não-hoje de propósito, para que esquecer de passar
     * o campo erre para o lado de NÃO pagar.
     */
    finishedDate?: string;
  },
): Promise<UpdateBookProgressResult> {
  if (!auth.currentUser) throw new Error("Utilizador não autenticado.");
  const fn = httpsCallable<
    {
      userBookId: string;
      newProgress: number;
      format?: string;
      totalPages?: number;
      finishedDate?: string;
    },
    UpdateBookProgressResult
  >(getFunctionsInstance(), "updateBookProgress");
  const res = await fn({
    userBookId,
    newProgress,
    ...(opts?.format !== undefined && { format: opts.format }),
    ...(opts?.totalPages !== undefined && { totalPages: opts.totalPages }),
    ...(opts?.finishedDate !== undefined && { finishedDate: opts.finishedDate }),
  });
  return res.data;
}

// ── Constantes de liga ────────────────────────────────────────────────────────

export const LEAGUE_LABELS: Record<LeagueTier, string> = {
  bronze:   "Liga de Bronze",
  silver:   "Liga de Prata",
  gold:     "Liga de Ouro",
  diamond:  "Liga de Diamante",
};

export const PROMOTE_COUNT  = 3;
export const RELEGATE_COUNT = 5;

export type RankZone = "promote" | "safe" | "relegate";

/**
 * @deprecated Para ligas globais use `resolveRankZone` em
 * `features/leagues/utils/leagueRules.ts` — varia por tier (Sprint F) e, desde
 * 17/08, por PONTUAÇÃO em sala com menos de 5 membros.
 *
 * Mantido apenas para ligas privadas, onde 3↑/5↓ é só visual — e ali é visual
 * mesmo: `closeWeeklyLeagues` filtra `type == "public"` e o `privateLeagueReset`
 * só zera o placar, então **nenhum** movimento de tier acontece em liga privada.
 * A zona pintada lá não corresponde a nada; é resíduo anterior a 17/08.
 */
export function getRankZone(rank: number, totalMembers: number): RankZone {
  if (rank <= PROMOTE_COUNT) return "promote";
  if (rank > totalMembers - RELEGATE_COUNT) return "relegate";
  return "safe";
}

// ── Sprint F §3.2: Vitrine de Liga ────────────────────────────────────────────

export interface LeagueSummary {
  weeksInBronze: number;
  weeksInSilver: number;
  weeksInGold: number;
  weeksInDiamond: number;
  championships: Partial<Record<LeagueTier, number>>;
  promotionsTotal: number;
  highestLeague: LeagueTier | null;
  firstReachedDiamondAt: Timestamp | null;
  lastClosedWeek: string | null;
  lastClosedTier: LeagueTier | null;
  lastClosedPosition: number | null;
  lastClosedTotalMembers: number | null;
  /** Placar da semana fechada, na métrica da sala. */
  lastClosedScore: number | null;
  lastClosedMetric: RankingMetric | null;
  /** Moedas creditadas pelo prêmio de colocação — já com o bônus PRO. */
  lastClosedCoins: number | null;
  lastClosedOutcome: "promoted" | "relegated" | "stayed" | null;
  lastClosedNewTier: LeagueTier | null;
  /**
   * Posto no top global DAQUELA semana, ou `null`.
   *
   * ⚠️ Não é o mesmo que `users.eliteRank`, e a diferença é a que evita uma
   * mentira: aquele campo só é reescrito para quem ESTÁ no top e o selo vale
   * 8 dias, então quem foi Elite na semana passada e não nesta continua com
   * ele preenchido e ainda "fresco". Aqui o posto é carimbado por semana —
   * ver `lastClosedEliteWeek`.
   */
  lastClosedEliteRank: number | null;
  /**
   * Semana a que `lastClosedEliteRank` se refere. Só vale quando é igual a
   * `lastClosedWeek`: o carimbo da Elite e o do resumo são escritas
   * separadas (o top global só existe depois do loop de salas), e uma delas
   * pode falhar sozinha.
   */
  lastClosedEliteWeek: string | null;
  /**
   * Badges que o último fechamento concedeu de NOVO — a porta do cosmético no
   * cartão de recap. Vazio = fechamento sem conquista nova (o comum); doc
   * anterior a 01/09 também cai em vazio, e o cartão simplesmente não desenha
   * a faixa.
   */
  lastClosedBadges: string[];
}

const EMPTY_SUMMARY: LeagueSummary = {
  weeksInBronze: 0,
  weeksInSilver: 0,
  weeksInGold: 0,
  weeksInDiamond: 0,
  championships: {},
  promotionsTotal: 0,
  highestLeague: null,
  firstReachedDiamondAt: null,
  lastClosedWeek: null,
  lastClosedTier: null,
  lastClosedPosition: null,
  lastClosedTotalMembers: null,
  lastClosedScore: null,
  lastClosedMetric: null,
  lastClosedCoins: null,
  lastClosedOutcome: null,
  lastClosedNewTier: null,
  lastClosedEliteRank: null,
  lastClosedEliteWeek: null,
  lastClosedBadges: [],
};

/**
 * Retorna o doc agregado `users/{uid}/league_summary/current` (escrito pelo
 * cron `closeWeeklyLeagues`). Devolve `null` se ainda não existe (cohort novo).
 */
export async function getLeagueSummary(uid: string): Promise<LeagueSummary | null> {
  if (!uid) return null;
  const ref = doc(db, "users", uid, "league_summary", "current");
  // Mesmo teto: sem ele a vitrine de Liga do perfil fica no skeleton pulsante
  // para sempre quando o transporte morre (o `loading` do `LeagueShowcase` é
  // derivado de o uid ter assentado, e sem assentar não há erro nem vazio).
  const snap = await withReadTimeout(getDoc(ref));
  if (!snap.exists()) return null;
  const data = snap.data() ?? {};
  return {
    ...EMPTY_SUMMARY,
    weeksInBronze: Number(data.weeksInBronze ?? 0),
    weeksInSilver: Number(data.weeksInSilver ?? 0),
    weeksInGold: Number(data.weeksInGold ?? 0),
    weeksInDiamond: Number(data.weeksInDiamond ?? 0),
    championships: (data.championships as LeagueSummary["championships"]) ?? {},
    promotionsTotal: Number(data.promotionsTotal ?? 0),
    highestLeague: (data.highestLeague as LeagueTier | undefined) ?? null,
    firstReachedDiamondAt: (data.firstReachedDiamondAt as Timestamp | undefined) ?? null,
    lastClosedWeek: (data.lastClosedWeek as string | undefined) ?? null,
    lastClosedTier: (data.lastClosedTier as LeagueTier | undefined) ?? null,
    lastClosedPosition: typeof data.lastClosedPosition === "number" ? data.lastClosedPosition : null,
    lastClosedTotalMembers: typeof data.lastClosedTotalMembers === "number" ? data.lastClosedTotalMembers : null,
    lastClosedScore: typeof data.lastClosedScore === "number" ? data.lastClosedScore : null,
    lastClosedMetric: (data.lastClosedMetric as RankingMetric | undefined) ?? null,
    lastClosedCoins: typeof data.lastClosedCoins === "number" ? data.lastClosedCoins : null,
    lastClosedOutcome:
      (data.lastClosedOutcome as LeagueSummary["lastClosedOutcome"] | undefined) ?? null,
    lastClosedNewTier: (data.lastClosedNewTier as LeagueTier | undefined) ?? null,
    lastClosedEliteRank:
      typeof data.lastClosedEliteRank === "number" ? data.lastClosedEliteRank : null,
    lastClosedEliteWeek:
      typeof data.lastClosedEliteWeek === "string" ? data.lastClosedEliteWeek : null,
    /*
     * Filtra por tipo item a item, e não um cast do array: o campo vem do
     * Firestore, e um doc escrito à mão no console com número ou objeto dentro
     * quebraria o `BADGE_CATALOG[badgeId]` do render. Id desconhecido já é
     * descartado pelo `BadgeRewardLine`; o que não pode chegar lá é não-string.
     */
    lastClosedBadges: Array.isArray(data.lastClosedBadges)
      ? data.lastClosedBadges.filter((b: unknown): b is string => typeof b === "string")
      : [],
  };
}

/** Retorna a soma total de semanas registradas no agregado. */
export function totalWeeksInSummary(summary: LeagueSummary): number {
  return (
    summary.weeksInBronze +
    summary.weeksInSilver +
    summary.weeksInGold +
    summary.weeksInDiamond
  );
}

// ── Frente G: ranking global ELITE ────────────────────────────────────────────

export interface GlobalRankingEntry extends LeagueIdentityFields {
  uid: string;
  displayName: string;
  photoURL: string;
  xp: number;
  rank: number;
}

export interface GlobalRanking {
  week: string;
  topReaders: GlobalRankingEntry[];
  totalRanked: number;
}

/** Ranking global da última semana fechada (escrito pelo closeWeeklyLeagues). */
export async function getGlobalRanking(): Promise<GlobalRanking | null> {
  const snap = await getDoc(doc(db, "global_rankings", "latest"));
  if (!snap.exists()) return null;
  const data = snap.data() ?? {};
  return {
    week: typeof data.week === "string" ? data.week : "",
    topReaders: Array.isArray(data.topReaders) ? (data.topReaders as GlobalRankingEntry[]) : [],
    totalRanked: Number(data.totalRanked ?? 0),
  };
}

/**
 * `global_rankings/latest` é um snapshot SEM validade própria: o
 * `closeWeeklyLeagues` só o reescreve quando a semana fechada produziu
 * `topReaders.length > 0`. Semana sem sala pública, sala já fechada ou todo
 * mundo com `xp === 0` deixam o doc congelado — e ele seguia sendo renderizado
 * como "Top leitores da semana" indefinidamente (era o mesmo pódio há ~1 mês).
 *
 * Fresco = a semana do snapshot é a última semana ISO fechada. Mesma ideia do
 * TTL do selo ELITE abaixo, que já esmaecia sozinho enquanto a seção da
 * /leagues não esmaecia — por isso o sintoma aparecia só na tela de Ligas.
 */
export function isGlobalRankingFresh(
  ranking: Pick<GlobalRanking, "week"> | null,
  now: Date = new Date(),
): boolean {
  if (!ranking?.week) return false;
  return ranking.week === previousISOWeekKey(now);
}

/**
 * O selo ELITE ("TOP N") vale ~8 dias após o fechamento — esmaece sozinho até
 * o próximo close (sem limpeza no servidor). `rankedAt` é o `eliteRankedAt` do
 * user (Timestamp do Firestore).
 */
const ELITE_TAG_TTL_MS = 8 * 24 * 60 * 60 * 1000;

export function isEliteRankActive(rankedAt?: Timestamp | null): boolean {
  const ms = rankedAt?.toMillis?.();
  return typeof ms === "number" && Date.now() - ms < ELITE_TAG_TTL_MS;
}

// ── Temporadas da liga (13/09, docs/systems/league-seasons.md §6.1) ─────────
//
// Leitura por documento, sem callable: `seasons/{id}` (escada top 100 +
// estrelas, doc pequeno como `global_rankings/latest`) e o acumulador da
// própria pessoa em `season_standings/{id}/entries/{uid}`. Qual temporada é
// "agora" o client resolve localmente pelo espelho `leagueSeasons.ts` — zero
// leitura só para saber a temporada.

export interface SeasonDoc {
  seasonId: string;
  startWeek: string;
  endWeek: string;
  status: "scheduled" | "active" | "closed";
  ladder: SeasonLadderEntry[];
  stars: SeasonStars | null;
  /** Meta coletiva (16/09): páginas críveis de toda a comunidade na temporada. */
  communityPages: number;
}

/**
 * O que o boundary grava por pessoa no fechamento (league-season-goals.md
 * §6.1) — é o que o cartão de fechamento lê, sem varrer nada.
 */
export interface SeasonFinal {
  rank: number | null;
  points: number;
  band: string;
  weeksScored: number;
  daysRead: number;
  peakTier: LeagueTier;
  stars: SeasonStarId[];
  awards: string[];
  proGrant: "comp14" | "comp30" | "gift14" | "gift30" | null;
  giftCode: string | null;
  resetFrom: LeagueTier | null;
  resetTo: LeagueTier | null;
}

export interface SeasonStanding {
  points: number;
  weeksScored: number;
  weeksTop1: number;
  weeksTop3: number;
  weeksInDiamond: number;
  /**
   * `null` = nenhum domingo fechou para esta pessoa ainda (BUG-319): a Meta
   * cria o doc com `daysRead` e SEM pico (`seasonDayCredit.ts`), e o antigo
   * `?? "bronze"` fazia a aba dizer "pico na Bronze ✓" a quem está na Prata+.
   */
  peakTier: LeagueTier | null;
  totalXp: number;
  totalPages: number;
  totalFocusMin: number;
  bestWeekXp: number;
  tierTrail: LeagueTier[];
  /** Meta da Temporada (16/09): dias distintos com leitura crível. */
  daysRead: number;
  /**
   * Último dia BR ("AAAA-MM-DD", `dayKeyForBR`) que a Meta contou — gravado por
   * `seasonDayCredit.ts` como guarda de idempotência. A aba Temporada v2 (05/10)
   * o lê para dizer "Hoje já contou". `null` = nenhum dia ainda / campo ausente.
   */
  lastDayReadKey: string | null;
  /** Só existe depois do fechamento da temporada. */
  final: SeasonFinal | null;
}

const n0 = (v: unknown): number => (typeof v === "number" && Number.isFinite(v) ? v : 0);

function parseSeasonFinal(raw: unknown): SeasonFinal | null {
  if (!raw || typeof raw !== "object") return null;
  const f = raw as Record<string, unknown>;
  const tier = (v: unknown): LeagueTier | null =>
    v === "bronze" || v === "silver" || v === "gold" || v === "diamond" ? v : null;
  const grant = f.proGrant;
  return {
    rank: typeof f.rank === "number" ? f.rank : null,
    points: n0(f.points),
    band: typeof f.band === "string" ? f.band : "III",
    weeksScored: n0(f.weeksScored),
    daysRead: n0(f.daysRead),
    peakTier: tier(f.peakTier) ?? "bronze",
    stars: Array.isArray(f.stars) ? (f.stars.filter((s) => typeof s === "string") as SeasonStarId[]) : [],
    awards: Array.isArray(f.awards) ? (f.awards.filter((s) => typeof s === "string") as string[]) : [],
    proGrant:
      grant === "comp14" || grant === "comp30" || grant === "gift14" || grant === "gift30" ? grant : null,
    giftCode: typeof f.giftCode === "string" ? f.giftCode : null,
    resetFrom: tier(f.resetFrom),
    resetTo: tier(f.resetTo),
  };
}

export async function getSeason(seasonId: string): Promise<SeasonDoc | null> {
  const snap = await getDoc(doc(db, "seasons", seasonId));
  if (!snap.exists()) return null;
  const data = snap.data() ?? {};
  return {
    seasonId,
    startWeek: typeof data.startWeek === "string" ? data.startWeek : "",
    endWeek: typeof data.endWeek === "string" ? data.endWeek : "",
    status: (data.status as SeasonDoc["status"] | undefined) ?? "active",
    ladder: Array.isArray(data.ladder) ? (data.ladder as SeasonLadderEntry[]) : [],
    stars: (data.stars as SeasonStars | undefined) ?? null,
    communityPages: n0(data.communityPages),
  };
}

export async function getSeasonStanding(
  seasonId: string,
  uid: string,
): Promise<SeasonStanding | null> {
  const snap = await getDoc(doc(db, "season_standings", seasonId, "entries", uid));
  if (!snap.exists()) return null;
  const d = snap.data() ?? {};
  return {
    points: n0(d.points),
    weeksScored: n0(d.weeksScored),
    weeksTop1: n0(d.weeksTop1),
    weeksTop3: n0(d.weeksTop3),
    weeksInDiamond: n0(d.weeksInDiamond),
    peakTier:
      d.peakTier === "bronze" || d.peakTier === "silver" || d.peakTier === "gold" || d.peakTier === "diamond"
        ? d.peakTier
        : null,
    totalXp: n0(d.totalXp),
    totalPages: n0(d.totalPages),
    totalFocusMin: n0(d.totalFocusMin),
    bestWeekXp: n0(d.bestWeekXp),
    tierTrail: Array.isArray(d.tierTrail) ? (d.tierTrail as LeagueTier[]) : [],
    daysRead: n0(d.daysRead),
    lastDayReadKey: typeof d.lastDayReadKey === "string" ? d.lastDayReadKey : null,
    final: parseSeasonFinal(d.final),
  };
}

/**
 * Quantas pessoas estão em cada degrau das metas da temporada — a visão do
 * `/admin/ligas/escada` (BACKLOG §0 item 10). É a leitura da 4ª semana do
 * roadmap ("pessoas com ≥ 20/40 dias") e o que diz, antes do boundary, quantas
 * Metas da Temporada vão ser concedidas.
 *
 * `count()` e não varredura: 1 leitura por 1.000 entradas de índice, por
 * contagem. `season_standings` é `read: isAuthed()` — nenhuma callable.
 * `points > 0` exclui a entrada que nasceu só com `daysRead` (league-season-
 * goals.md: a entry pode existir antes de a pessoa pontuar na escada).
 */
export interface SeasonGoalCounts {
  entries: number;
  scored: number;
  days20: number;
  days30: number;
  daysMeta: number;
}

export async function countSeasonGoals(
  seasonId: string,
  metaDays: number,
): Promise<SeasonGoalCounts> {
  const entries = collection(db, "season_standings", seasonId, "entries");
  const count = async (...filters: ReturnType<typeof where>[]) =>
    (await withReadTimeout(getCountFromServer(query(entries, ...filters)))).data().count;
  const [all, scored, days20, days30, daysMeta] = await Promise.all([
    count(),
    count(where("points", ">", 0)),
    count(where("daysRead", ">=", 20)),
    count(where("daysRead", ">=", 30)),
    count(where("daysRead", ">=", metaDays)),
  ]);
  return { entries: all, scored, days20, days30, daysMeta };
}

/**
 * As duas contagens do Hall dos Campeões por temporada fechada (24/09): quem
 * pontuou (é quem leva o flair se a meta coletiva bateu) e quem fechou a Meta
 * dos 40 dias. Duas `count()` — nenhuma varredura, nenhuma callable.
 */
export async function countSeasonHall(
  seasonId: string,
  metaDays: number,
): Promise<{ scored: number; meta: number }> {
  const entries = collection(db, "season_standings", seasonId, "entries");
  const [scored, meta] = await Promise.all([
    withReadTimeout(getCountFromServer(query(entries, where("points", ">", 0)))),
    withReadTimeout(getCountFromServer(query(entries, where("daysRead", ">=", metaDays)))),
  ]);
  return { scored: scored.data().count, meta: meta.data().count };
}

/**
 * Cartão de abertura da temporada visto — `users.shownSeasonIntros` é
 * client-safe (rules: lista ≤ 20). `arrayUnion` é idempotente.
 */
export async function markSeasonIntroShown(uid: string, seasonId: string): Promise<void> {
  await updateDoc(doc(db, "users", uid), { shownSeasonIntros: arrayUnion(seasonId) });
}

// ── Convite pelo app (06/10, `docs/systems/league-invite-in-app.md`) ──────────
export type InvitePersonStatus = "sent" | "already_invited" | "already_member" | "recipient_busy";

/** Teto diário de quem convida — espelho de `CLUB_INVITE_SENDER_DAILY`. */
export const CLUB_INVITE_SENDER_DAILY = 5;

/**
 * Convida uma pessoa (que eu sigo ou que me segue) para a liga. Erros chegam
 * com `details.reason`: `target_unavailable`, `sender_daily_limit`,
 * `room_full`, `not_a_member`.
 */
export async function invitePersonToPrivateLeagueCallable(
  roomId: string,
  targetUid: string,
): Promise<InvitePersonStatus> {
  if (!auth.currentUser) throw new Error("Utilizador não autenticado.");
  const fn = httpsCallable<{roomId: string; targetUid: string}, {status: InvitePersonStatus}>(
    getFunctionsInstance(),
    "invitePersonToPrivateLeague",
  );
  const res = await fn({roomId, targetUid});
  return res.data.status;
}
